import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Plus, RefreshCw, Search } from "lucide-react";
import { api, formatDate, formatPhone } from "../api";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import Dialog from "../components/Dialog";
import FilterPill from "../components/FilterPill";
import ContactDrawer from "../components/crm/ContactDrawer";
import useNotice from "../components/crm/useNotice";
import type { ContactRow, Person } from "../components/crm/types";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import { STAGES, contactName, money, stageLabel } from "../crm";
import { useContactParam } from "./CrmPipeline";

const SOURCES = [
  { value: "all", name: "All sources" },
  { value: "form", name: "Forms" },
  { value: "booking", name: "Booking pages" },
  { value: "phone_call", name: "Phone calls" },
  { value: "form_left", name: "Left a form" },
  { value: "manual", name: "Added by hand" },
];
const PAGE = 100;

/** Everyone in the CRM, newest activity first. A row opens the contact. */
export default function CrmContacts() {
  const contacts = useApi<ContactRow[]>("/api/crm/contacts");
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [contactId, openContact] = useContactParam();
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState("all");
  const [source, setSource] = useState("all");
  const [owner, setOwner] = useState("all");
  const [shown, setShown] = useState(PAGE);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useNotice();
  const all = contacts.data || [];
  const needle = query.trim().toLowerCase();
  const rows = useMemo(() => all.filter((c) =>
    (stage === "all" || (stage === "none" ? !c.stage : c.stage === stage)) &&
    (source === "all" || c.source === source) &&
    (owner === "all" || (owner === "none" ? !c.owner_id : String(c.owner_id) === owner)) &&
    (!needle || `${c.first_name} ${c.last_name} ${c.email} ${c.company} ${c.phone}`.toLowerCase().includes(needle))), [all, stage, source, owner, needle]);
  useEffect(() => setShown(PAGE), [stage, source, owner, needle]);
  const filtered = Boolean(needle) || stage !== "all" || source !== "all" || owner !== "all";
  const clear = () => { setQuery(""); setStage("all"); setSource("all"); setOwner("all"); };
  const owners = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of all) if (c.owner_id && c.owner_name) seen.set(String(c.owner_id), c.owner_name);
    return [{ value: "all", name: "Anyone" }, ...[...seen].map(([value, name]) => ({ value, name })), { value: "none", name: "Unassigned" }];
  }, [all]);
  const newContact = canWrite && <button className="button" onClick={() => setCreating(true)}><Plus aria-hidden="true" />New contact</button>;

  return (
    <>
      <PageHeader title="Contacts" actions={newContact} />
      <div className="crm-toolbar">
        <label className="search-control"><Search aria-hidden="true" /><input aria-label="Search contacts" placeholder="Search name, email, company or phone" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
        <FilterPill label="Stage" value={stage} onChange={setStage} options={[{ value: "all", name: "Any stage" }, ...STAGES.map((s) => ({ value: s.id, name: s.label })), { value: "none", name: "No deal" }]} />
        <FilterPill label="Source" value={source} onChange={setSource} options={SOURCES} />
        <FilterPill label="Owner" value={owner} onChange={setOwner} options={owners} />
        <span className="toolbar-end">
          {filtered && <button type="button" className="text-button" onClick={clear}>Clear filters</button>}
          <span className="result-count" aria-live="polite">{contacts.loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "contact" : "contacts"}`}</span>
          <button className="icon-button" aria-label="Refresh contacts" title="Refresh contacts" disabled={contacts.refreshing} onClick={contacts.reload}><RefreshCw className={contacts.refreshing ? "spin" : ""} aria-hidden="true" /></button>
        </span>
      </div>
      {notice && <p className="inline-notice" role="status">{notice}</p>}
      {contacts.loading && !contacts.data ? <PageSkeleton cards={0} />
        : contacts.error ? <ErrorState message={contacts.error} onRetry={contacts.reload} />
        : !all.length ? <section className="panel"><EmptyState title="No contacts yet" description="People who fill in a form or book a call show up here." action={newContact || undefined} /></section>
        : !rows.length ? <section className="panel"><EmptyState title="No matching contacts" description="Try another search or filter." action={<button className="button secondary" onClick={clear}>Clear filters</button>} /></section>
        : (
          <section className="panel table-wrap">
            <table className="module-table crm-table crm-contacts-table">
              <thead><tr><th>Name</th><th>Company</th><th>Email</th><th>Phone</th><th>Stage</th><th className="num">Amount</th><th>Owner</th><th>Last activity</th></tr></thead>
              <tbody>
                {rows.slice(0, shown).map((c) => (
                  <tr key={c.id} className="is-clickable" onClick={() => openContact(c.id)}>
                    <td data-label="Name"><button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); openContact(c.id); }}>{contactName(c)}</button></td>
                    <td data-label="Company" data-empty={c.company ? undefined : ""}>{c.company || <span className="crm-muted">—</span>}</td>
                    <td data-label="Email" className="crm-ellipsis" data-empty={c.email ? undefined : ""}>{c.email ? <a className="crm-cell-link" href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>{c.email}</a> : <span className="crm-muted">—</span>}</td>
                    <td data-label="Phone" data-empty={c.phone ? undefined : ""}>{c.phone ? <a className="crm-cell-link" href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}>{formatPhone(c.phone)}</a> : <span className="crm-muted">—</span>}</td>
                    <td data-label="Stage">{c.stage ? <span className={`crm-stage s-${c.stage}`}>{stageLabel(c.stage)}</span> : <span className="crm-muted">No deal</span>}</td>
                    <td data-label="Amount" className="num" data-empty={c.value_cents ? undefined : ""}>{money(c.value_cents) || <span className="crm-muted">—</span>}</td>
                    <td data-label="Owner">{c.owner_name || <span className="crm-muted">Unassigned</span>}</td>
                    <td data-label="Last activity">{formatDate(c.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > shown && <div className="crm-more"><button className="button secondary small" onClick={() => setShown((n) => n + PAGE)}>Show more</button></div>}
          </section>
        )}
      {creating && <NewContactDialog onClose={() => setCreating(false)} onCreated={(msg, id) => { setCreating(false); setNotice(msg); contacts.reload(); openContact(id); }} />}
      {contactId && <ContactDrawer key={contactId} id={contactId} onClose={() => openContact(null)} onChanged={contacts.reload} />}
    </>
  );
}

function NewContactDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (msg: string, id: number) => void }) {
  const people = useApi<Person[]>("/api/tasks/assignees");
  const { user } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", title: "", owner_id: user ? String(user.id) : "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const c = await api<{ id: number; existing: boolean; first_name: string; last_name: string; email: string }>("/api/crm/contacts", { method: "POST", body: JSON.stringify({ ...f, owner_id: f.owner_id || null }) });
      onCreated(c.existing ? `${contactName(c)} was already here, so we opened that contact.` : `${contactName(c)} added.`, c.id);
    } catch (err) { setError(err instanceof Error ? err.message : "The contact could not be added."); }
    finally { setBusy(false); }
  }
  return (
    <Dialog title="New contact" onClose={onClose}>
      <form className="operation-form crm-edit" onSubmit={submit}>
        <div className="crm-edit-grid">
          <label>First name<input value={f.first_name} onChange={set("first_name")} maxLength={80} required /></label>
          <label>Last name<input value={f.last_name} onChange={set("last_name")} maxLength={80} /></label>
          <label>Email<input type="email" value={f.email} onChange={set("email")} maxLength={200} /></label>
          <label>Phone<input type="tel" value={f.phone} onChange={set("phone")} maxLength={40} /></label>
          <label>Company<input value={f.company} onChange={set("company")} maxLength={160} /></label>
          <label>Job title<input value={f.title} onChange={set("title")} maxLength={120} /></label>
          <label>Owner
            <select value={f.owner_id} onChange={set("owner_id")}>
              <option value="">Unassigned</option>
              {(people.data || []).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </label>
        </div>
        {error && <p className="crm-error" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button" disabled={busy}>{busy ? "Adding…" : "Add contact"}</button>
        </div>
      </form>
    </Dialog>
  );
}
